import { RESULTS_DATA } from '@/features/landing-page/helpers/landing-results-data'

/**
 * View-model for the lp-v3 "Loved by customers" testimonial carousel. The design (a big quote + a
 * PLAY VIDEO thumbnail + author/company) needs quote + avatar + company logo + video per entry —
 * which the old five-item "Loved by customers" list does NOT have (those are LinkedIn screenshots).
 * So the entries are the named, video-backed testimonials the mockup actually shows: the HubSpot
 * podcast quote plus case-study quotees REUSED from `RESULTS_DATA` (single source of truth). Adjust
 * the `id` list / order below to change which testimonials appear.
 */
export type TestimonialVideo = { kind: 'youtube'; embedId: string } | { kind: 'video'; src: string }

export type Testimonial = {
    id: string
    quote: string
    authorName: string
    authorTitle: string
    company: string
    companyLogoUrl?: string
    avatarUrl: string
    /** Optional — only HubSpot currently ships a playable clip; others are quote-only. */
    video?: TestimonialVideo
    /** External poster (e.g. YouTube thumbnail); mp4 entries show their own first frame instead. */
    posterUrl?: string
}

/** The case-study quotes are wrapped in curly quotes in the data — the card adds its own, so strip. */
const stripQuotes = (value: string) => value.replace(/^[“"]/, '').replace(/[”"]$/, '').trim()

/** HubSpot podcast testimonial — from the old `landing-video-section` (not in RESULTS_DATA). */
const KIPP_BODNAR: Testimonial = {
    id: 'kipp-bodnar',
    quote: 'The best autonomous AI marketing agent that we have seen so far.',
    authorName: 'Kipp Bodnar',
    authorTitle: 'CMO, HubSpot',
    company: 'HubSpot',
    companyLogoUrl: '/icons/hubspot.svg',
    avatarUrl: '/assets/landing-page/kipp-bodnar-avatar.png',
    video: { kind: 'youtube', embedId: '9LNNAQbKLdc' },
    posterUrl: 'https://img.youtube.com/vi/9LNNAQbKLdc/hqdefault.jpg',
}

/** Case-study quotees with avatars — quote-only (video is HubSpot-only per design). */
const CASE_STUDY_TESTIMONIAL_IDS = ['taxfix', 'sumup', 'lila', 'stromnow']

const fromCaseStudies = CASE_STUDY_TESTIMONIAL_IDS.flatMap((id): Testimonial[] => {
    const result = RESULTS_DATA.find((entry) => entry.id === id)
    if (!result || !result.quotee.avatarUrl) return []

    return [
        {
            id: result.id,
            quote: stripQuotes(result.quote),
            authorName: result.quotee.name,
            authorTitle: result.quotee.title,
            company: result.company,
            companyLogoUrl: result.logoUrl,
            avatarUrl: result.quotee.avatarUrl,
        },
    ]
})

export const TESTIMONIALS: Testimonial[] = [KIPP_BODNAR, ...fromCaseStudies]
